interface SseEvent {
  name: string | null
  data: unknown
}

interface StreamedSseResponseOptions {
  onEvent: (event: SseEvent) => void
  // Any bytes at all, including keep-alive comments, so callers can tell a live
  // stream from a half-open socket.
  onActivity?: () => void
}

const EVENT_TERMINATOR = '\n\n'

function parseEvent(raw: string): SseEvent | null {
  let name: string | null = null
  const dataLines: string[] = []

  for (const line of raw.split('\n')) {
    if (line === '' || line.startsWith(':')) continue
    if (line.startsWith('event:')) {
      name = line.slice('event:'.length).trim()
    } else if (line.startsWith('data:')) {
      dataLines.push(line.slice('data:'.length).trim())
    }
  }

  if (name === null && dataLines.length === 0) return null

  let data: unknown = null
  if (dataLines.length > 0) {
    try {
      data = JSON.parse(dataLines.join('\n'))
    } catch {
      data = dataLines.join('\n')
    }
  }

  return { name, data }
}

export async function streamedSseResponse(
  response: Response,
  { onEvent, onActivity }: StreamedSseResponseOptions,
): Promise<void> {
  const reader = response.body?.getReader()
  if (!reader) throw new Error('Response body is not readable')

  const decoder = new TextDecoder()
  let buffer = ''

  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      onActivity?.()
      buffer += decoder.decode(value, { stream: true })

      let terminatorIndex = buffer.indexOf(EVENT_TERMINATOR)
      while (terminatorIndex !== -1) {
        const raw = buffer.slice(0, terminatorIndex)
        buffer = buffer.slice(terminatorIndex + EVENT_TERMINATOR.length)
        const event = parseEvent(raw)
        if (event) onEvent(event)
        terminatorIndex = buffer.indexOf(EVENT_TERMINATOR)
      }
    }
  } finally {
    reader.releaseLock()
  }
}
